// Centralized help content configuration for easy maintenance
export const helpContent = [
    {
        title: "Quick Start Guide",
        category: "getting-started",
        defaultExpanded: true,
        subtopics: [
            {
                title: "Connect",
                instruction: "Click 'Login' in the top right, then connect your route source of choice to access your saved routes. This allows you to import all your existing routes automatically.",
                screenshot: ["login.png", "connection_required.png"]
            },
            {
                title: "Select",
                instruction: "Choose from your routes and save them for offline use. You can search and filter routes by distance, elevation, or surface type.",
                screenshot: ["route_list.png", "route_detail.png", "filter_routes.png", "search_routes.png"]
            },
            {
                title: "Save",
                instruction: "Get detailed elevation profiles and points of interest in an easily readable format. The cue cards are designed for easy reading while cycling.",
                screenshot: ["route_detail_downloaded.png", "chart_poi.png"]
            }
        ]
    },
    {
        title: "Finding Your Route",
        category: "features",
        subtopics: [
            {
                title: "Find the route in the list",
                instruction: "Browse through your available routes in the main route list.",
                screenshot: "route_list.png"
            },
            {
                title: "Search your routes",
                instruction: "Use the search functionality to quickly find specific routes by name or criteria.",
                screenshot: "search_routes.png"
            },
            {
                title: "View a specific public route",
                instruction: "Access and view public routes shared by other users.",
                screenshot: "view_public_route.png"
            },
            {
                title: "Filter your routes",
                instruction: "Filter your routes by download status and source.",
                screenshot: "filter_routes.png"
            }
        ]
    },
    {
        title: "Using Cue Cards Offline",
        category: "offline",
        subtopics: [
            {
                title: "Save Routes for Offline Use",
                instruction: "Before your adventure, save your routes for offline access. This ensures you can view your cue cards even without internet connection.",
                screenshot: "route_detail_downloaded.png"
            },
            {
                title: "Offline Route Configuration",
                instruction: "Once a route is downloaded, your configuartion of the chart and POI descriptions is saved with the route. You can change the configuration at any time in the route settings.",
                screenshot: ["route_configuration.png", "charts.png"]
            },
            {
                title: "Installing CueMeUp",
                instruction: "Install CueMeUp on your device for a native app experience. Works on both mobile and desktop devices.",
                screenshot: ["add_to_homescreen.png", "install_from_web.png", "install_done.png"]
            },
            {
                title: "When to Update Your Saved Offline Route",
                instruction: "You should update your saved route whenever you make changes in RWGPS, including: modifying the route path, adding/removing waypoints, updating POIs, or changing route descriptions. This ensures your offline cue cards are always current.",
            },
        ]
    },
    {
        title: "Chart Configuration",
        category: "settings",
        subtopics: [
            {
                title: "Access Chart Settings",
                instruction: "Click the settings icon on any route page to view chart settings.",
                screenshot: "charts.png"
            },
            {
                title: "Chunk Distance",
                instruction: "Configure the distance intervals for chart segments to optimize readability.",
            },
            {
                title: "Show POI Descriptions",
                instruction: "Control whether Point of Interest descriptions are displayed on the charts.",
            },
            {
                title: "Show Distance to Next POI",
                instruction: "Display the distance to the next Point of Interest on the charts.",
            },
            {
                title: "Hide Off-Route POI",
                instruction: "Hide all POIs that are further from the route than the off-route threshold.",
            },
            {
                title: "POI Font Size",
                instruction: "Adjust the font size for Points of Interest descriptions for better readability.",
            },
            {
                title: "Off-Route Distance Threshold",
                instruction: "Set the distance threshold for off-route detection warnings.",
                screenshot: "off_route_poi.png"
            },
            {
                title: "Uniform Elevation Scale",
                instruction: "Set consistent elevation scaling across all chart segments for better comparison.",
            },
            {
                title: "Side by Side Layout",
                instruction: "Choose between different layout options for displaying elevation charts and POI information.",
            },
            {
                title: "Show Grid",
                instruction: "Toggle grid lines on elevation charts for easier reading of values and distances.",
            },
            {
                title: "Elevation Points Density",
                instruction: "Adjust the density of elevation data points shown on the chart for optimal detail level.",
            },
            {
                title: "Chart Height",
                instruction: "Set the height of elevation charts to match your viewing preferences.",
            }
        ]
    },
    {
        title: "Points of Interest (POI)",
        category: "features",
        subtopics: [
            {
                title: "POI Management",
                instruction: "View and manage Points of Interest in your route planner of choice. POIs include rest stops, landmarks, and important navigation points.",
            },
            {
                title: "POI Descriptions",
                instruction: "Make sure to include descriptions for important POIs. These can be optionally displayed using the chart configuration.",
                screenshot: ["enable_descriptions.png", "poi_descriptions.png"]
            }
        ]
    },
    {
        title: "Route Editing & Route Sources",
        category: "integration",
        subtopics: [
            {
                title: "Route Source Integrations",
                instruction: [
                    "CueMeUp integrates seamlessly with several route sources. More information about supported integrations can be found in the ",
                    { type: 'link', text: 'Integrations', to: '/integrations' },
                    " page."
                ],
            },
        ]
    },
    {
        title: "Location Tracking",
        category: "features",
        subtopics: [
            {
                title: "Enable Location Tracking",
                instruction: "Go to Settings and toggle 'Show User Location' to enable real-time position updates. This helps you see exactly where you are on the elevation profile and what's coming up next.",
                screenshot: ["app_settings_menu.png", "location_enabled.png", "location_on_chart.png"]
            }
        ]
    },
    {
        title: "Troubleshooting",
        category: "support",
        subtopics: [
            {
                title: "Common Issues",
                instruction: "If you're experiencing problems with the app, try refreshing the page first. You can restart the app by making sure to close all tabs of the application.",
            },
            {
                title: "Route Import Problems",
                instruction: "If your routes aren't importing from Ride With GPS, check that your account is properly connected. You may need to disconnect and reconnect your RWGPS account.",
            },
            {
                title: "Performance Issues",
                instruction: "If the app is running slowly, try closing other browser tabs or applications.",
            }
        ]
    },
    {
        title: "Account & Global Settings",
        category: "account",
        subtopics: [
            {
                title: "Unit System",
                instruction: "Change the unit system for the app, including distance and elevation. ",
            },
            {
                title: "Route Sources",
                instruction: "Manage your connected route sources."
            },
        ]
    },
    {
        title: "Still Need Help",
        category: "support",
        subtopics: [
            {
                title: "Contact Support",
                instruction: "Can't find what you're looking for? We're here to help! Contact us directly for personalized assistance.",
            },
            {
                title: "Feedback Form",
                instruction: "Share your feedback, suggestions, or report issues using our in-app feedback form. Your input helps us improve CueMeUp for all cyclists.",
            }
        ]
    }
];
